import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, useFormikContext } from 'formik';
import { observer } from 'mobx-react';
import { Grid } from '@material-ui/core';
import { isAfter, isSameDay } from 'date-fns';
import { Divider } from '@material-ui/core';
import { lookupsJson, offerExcessUM, getOptions, noUmbrellaStates, umbrellaStartDateStates } from '@ourbranch/lookups';

import { NotificationCard } from 'core/components/notification-card';
import useSession from 'core/hooks/use-session';
import Field from 'core/components/form/form.v2';
import { Label } from 'core/components/label';
import Section from 'core/components/section';
import { awsDateToDateObject, cleanObject } from 'core/helpers/formatters';
import { selectOptions } from 'core/helpers/select';
import { canAddUmbrellaUMUIM, canAddUmbrella } from 'core/helpers/quoter.service';
import { useStore } from 'core/store';
import useStyles from './umbrella.styles';
import PropertiesForm from './properties-form';

const id = 'umbrellaCoverage';
const mpdCodesUmbrellaMap = {
  // removing umbrella coverage
  ARU: 'AR',
  ARUS: 'ARS',
  AU: 'A',
  AUS: 'AS',
  HRU: 'HR',
  HU: 'H',
  CRU: 'CR',
  CU: 'C',
  // adding umbrella coverage
  C: 'CU',
  CR: 'CRU',
  AR: 'ARU',
  ARS: 'ARUS',
  A: 'AU',
  AS: 'AUS',
  HR: 'HRU',
  H: 'HU'
};

export const formatUmbrellaValues = (values, includeUmbrellaCoverage = true) => {
  // If values is null/undefined or umbrella coverage is not included, return null
  if (!values || !includeUmbrellaCoverage) {
    return null;
  }

  /**
   * typeof item === 'object' below is a temporary fix until the use case is properly handled
   * @TODO: Support free input addresses for rental properties
   */
  const otherPropertyAddresses = (values.otherPropertyAddresses || []).filter(
    (item) => !!item && typeof item === 'object'
  );
  const rentalPropertyAddresses = (values.rentalPropertyAddresses || []).filter(
    (item) => !!item && typeof item === 'object'
  );
  const watercraftHullLengths = (values.watercraftHullLengths || []).filter((item) => !!item);
  return {
    ...values,
    watercraftHullLengths,
    rentalPropertyAddresses,
    numRentalProperties: rentalPropertyAddresses.length,
    otherPropertyAddresses,
    numOtherProperties: otherPropertyAddresses.length
  };
};

const mockUmbrellaValues = {
  watercraftHullLengths: [],
  rentalPropertyAddresses: [],
  otherPropertyAddresses: [],
  numRVs: null,
  numMotorcyclesScooters: null,
  numATVs: null,
  numPersonalWatercraft: null,
  liabilityCoverageLimit: null,
  numVehiclesTotal: null,
  numLicensedDriversUnder25: null
};

const Umbrella = ({ fromPolicy, disabled, coverageX, state }) => {
  const classes = useStyles();
  const { values, setFieldValue, setFieldTouched, errors, touched } = useFormikContext();

  const { umbrellaCoverage, includeUmbrella, autoCoverage, drivers } = values;

  const [cleaned, setCleaned] = useState(false);
  const [canOfferUmbrella, setCanOfferUmbrella] = useState(false);
  const [canAddUM, setCanAddUM] = useState(false);
  const {
    offer: offerStore,
    account: {
      policies: { policy: policyStore }
    }
  } = useStore();

  const { quote } = offerStore?.offer || {};

  const { policyLimitUMBI, policyLimitUIMBI, policyLimitBIPD } = autoCoverage || {};

  const { isAgency } = useSession();

  // for all of the below, if autoCoverage doesn't exist, this is a policy, and we don't
  // re-validate on policies for umbrella eligibility based upon auto.
  // in a Bix offer or state Bix policy and has 500 umbi and uimbi;

  useEffect(() => {
    setCanAddUM(canAddUmbrellaUMUIM({ policyLimitUMBI, policyLimitUIMBI, state }) && offerExcessUM[state]);
    setCanOfferUmbrella(
      canAddUmbrella({
        policyLimitBIPD,
        coverageX,
        drivers: drivers || quote?.drivers,
        global: fromPolicy ? policyStore.policyDetails.global : quote?.global,
        includeUmbrella: fromPolicy ? policyStore.policyDetails.includeUmbrella : false
      })
    );
  }, [policyLimitUMBI, policyLimitUIMBI, state, fromPolicy, quote, policyStore, drivers, policyLimitBIPD, coverageX]);

  const validUMUIMOptions = getOptions('uninsuredMotoristLimit', state);

  // always show umbrella in policy (but will be disabled if not correct auto coverages)
  // only show umbrella in offer if it's a Home with Bundle Discount or a Home/Condo and Auto Bundle
  const offerOptionsAllowingUmbrella = ['HB', 'HA', 'CA'];
  let umbrellaInThisState = !noUmbrellaStates[state];
  if (umbrellaStartDateStates[state]) {
    const rateControlDate = fromPolicy
      ? new Date()
      : awsDateToDateObject(offerStore?.offer?.quote?.global.rateControlDate);
    const stateDate = new Date(umbrellaStartDateStates[state]);
    umbrellaInThisState = isAfter(rateControlDate, stateDate) || isSameDay(rateControlDate, stateDate);
  }
  const showUmbrellaSection =
    umbrellaInThisState &&
    (fromPolicy ||
      (offerOptionsAllowingUmbrella.includes(offerStore?.selectedOption) && quote?.offerings.offerUmbrella));

  const umbrellaOpts = lookupsJson.umbrellaLimit.filter((d) => {
    if (fromPolicy && policyStore.policyDetails?.umbrellaCoverage?.liabilityCoverageLimit === 2000000) {
      return d.id <= 2000000;
    }
    return d.id <= 1000000;
  });

  useEffect(() => {
    // if we add umbrella we add the defaults to the form
    if (includeUmbrella) {
      if (!umbrellaCoverage) {
        setCleaned(false);
        setFieldValue(
          id,
          {
            ...mockUmbrellaValues
          },
          false
        );
      } else if (!cleaned) {
        setCleaned(true);
        setFieldValue(id, { ...mockUmbrellaValues, ...cleanObject({ ...umbrellaCoverage }) }, false);
      }
    } else {
      // When umbrella is turned off, set to null
      setFieldValue(id, null, false);
      setCleaned(false);
    }
  }, [includeUmbrella, setFieldValue, umbrellaCoverage, setCleaned, cleaned]);

  return (
    showUmbrellaSection && (
      <Section title="Umbrella Coverage" type="SubSection">
        <div className={classes.containerDark}>
          <Form disabled={disabled}>
            <Grid container spacing={4} alignItems="flex-end">
              <Field
                id="includeUmbrella"
                name="includeUmbrella"
                type="switch"
                label="Umbrella Coverage"
                mode="dark"
                xs={12}
                onChange={() => {
                  if (values.multiPolicyDiscount && mpdCodesUmbrellaMap[values.multiPolicyDiscount]) {
                    setFieldTouched('multiPolicyDiscount', true);
                    setFieldValue('multiPolicyDiscount', mpdCodesUmbrellaMap[values.multiPolicyDiscount]);
                  }
                }}
                disabled={!canOfferUmbrella && !values.includeUmbrella && !errors.includeUmbrella}
                fast={false}
              />
              {!canOfferUmbrella && (!errors.includeUmbrella || !touched.includeUmbrella) && (
                <NotificationCard type="tertiary">
                  Umbrella is disabled since underlying policies do not meet the following requirements: Auto BI needs
                  to be at least $500K per occurrence ($250K/$500K for split or $500K for CSL), homeowner&lsquo;s
                  liability needs to be at least $300K, there must not be any excluded drivers or drivers with a UDR,
                  and there must be no more than 2 incidents total, or the applicant's prior carrier is on the list of
                  non-standard carriers.
                </NotificationCard>
              )}
              {includeUmbrella && umbrellaCoverage && (
                <>
                  <Field
                    id={`${id}.liabilityCoverageLimit`}
                    name={`${id}.liabilityCoverageLimit`}
                    type="select"
                    label="Umbrella Limit"
                    mode="dark"
                    xs={6}
                    options={umbrellaOpts}
                  />
                  {!isAgency && (
                    <Field
                      id={`${id}.highRisk`}
                      name={`${id}.highRisk`}
                      type="checkbox"
                      label="High risk coverage?"
                      mode="dark"
                      xs={6}
                    />
                  )}
                  <Field
                    id={`${id}.numVehiclesTotal`}
                    name={`${id}.numVehiclesTotal`}
                    type="select"
                    label="Vehicles"
                    mode="dark"
                    xs={6}
                    options={selectOptions(0, 10)}
                  />
                  <Field
                    id={`${id}.numLicensedDriversUnder25`}
                    name={`${id}.numLicensedDriversUnder25`}
                    type="select"
                    label="Licensed Drivers under 25 in your home"
                    mode="dark"
                    options={selectOptions(0, 10)}
                    xs={6}
                  />
                  <Field
                    id={`${id}.numRVs`}
                    name={`${id}.numRVs`}
                    type="select"
                    label="Number of golf carts and snowmobiles"
                    mode="dark"
                    xs={8}
                    options={selectOptions(0, 2)}
                  />
                  <Field
                    id={`${id}.presenceOfBusiness`}
                    name={`${id}.presenceOfBusiness`}
                    type="checkbox"
                    label="Business at home"
                    mode="dark"
                    xs={4}
                  />
                  {offerExcessUM[state] && (
                    <Grid container item xs={12}>
                      <Field
                        id={`${id}.uninsuredMotoristLimit`}
                        name={`${id}.uninsuredMotoristLimit`}
                        type="select"
                        options={validUMUIMOptions}
                        label="Un/Underinsured Motorist BI"
                        mode="dark"
                        xs={6}
                      />
                    </Grid>
                  )}
                  {canAddUM && validUMUIMOptions.length === 1 && (
                    <>
                      <Grid item xs={12} />
                      <Grid item xs={12}>
                        <Label type="smallLightLabel">
                          For additional UM options, a &quot;dev escalation&quot; is required.
                        </Label>
                      </Grid>
                    </>
                  )}
                  {!offerExcessUM[state] && (
                    <>
                      <Grid item xs={12} />
                      <Grid item xs={12}>
                        <Label type="smallLightLabel">
                          IMPORTANT NOTICE: PERSONAL UMBRELLA LIABILITY COVERAGE DOES NOT PROVIDE UNINSURED OR
                          UNDERINSURED MOTORISTS COVERAGE
                        </Label>
                      </Grid>
                    </>
                  )}
                  <Field
                    id={`${id}.numMotorcyclesScooters`}
                    name={`${id}.numMotorcyclesScooters`}
                    type="select"
                    label="Number of Motorcycles/Scooters"
                    mode="dark"
                    xs={6}
                    options={selectOptions(0, 5)}
                  />
                  <Field
                    id={`${id}.numATVs`}
                    name={`${id}.numATVs`}
                    type="select"
                    label="Number of ATV"
                    mode="dark"
                    xs={6}
                    options={selectOptions(0, 5)}
                  />
                  <Field
                    id={`${id}.numPersonalWatercraft`}
                    name={`${id}.numPersonalWatercraft`}
                    type="select"
                    label="Personal Watercraft"
                    mode="dark"
                    xs={6}
                    options={selectOptions(0, 5)}
                  />
                  <Divider className={classes.divider} />
                  <Grid container item spacing={2}>
                    <Grid item xs={12}>
                      <Label type="formSubTitle">
                        Number of Boats: {(umbrellaCoverage?.watercraftHullLengths?.filter(Boolean) || []).length}
                      </Label>
                    </Grid>
                    <Field
                      id={`${id}.watercraftHullLengths`}
                      name={`${id}.watercraftHullLengths`}
                      fast={false}
                      type="array"
                      label="Watercraft Hull Length"
                      mode="dark"
                      fieldProps={{ type: 'numeric', xs: 6, max: 5 }}
                    />
                  </Grid>
                </>
              )}
            </Grid>
            {includeUmbrella && (
              <>
                <Divider className={classes.divider} />
                <PropertiesForm id={id} />
              </>
            )}
          </Form>
        </div>
      </Section>
    )
  );
};

Umbrella.propTypes = {
  disabled: PropTypes.bool,
  fromPolicy: PropTypes.bool,
  coverageX: PropTypes.number.isRequired,
  state: PropTypes.string.isRequired
};

Umbrella.defaultProps = {
  disabled: false,
  fromPolicy: false
};

export default observer(Umbrella);
